import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { FiBell, FiLogIn, FiSend, FiPlus, FiEdit, FiDatabase, FiCalendar, FiShield } from 'react-icons/fi';

const ACTION_ICONS = {
  LOGIN: <FiLogIn />, SEND: <FiSend />, CREATE: <FiPlus />,
  UPDATE: <FiEdit />, UPLOAD: <FiDatabase />, SCHEDULE: <FiCalendar />,
};

const getIcon = (action) => {
  const key = Object.keys(ACTION_ICONS).find(k => action?.toUpperCase().includes(k));
  return ACTION_ICONS[key] || <FiShield />;
};

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function NotificationBell({ role = 'admin' }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifications = () => {
      // Use role-specific endpoint
      const endpoint = role === 'admin' ? '/admin/audit-logs?limit=8' : '/user/activity?limit=8';
      api.get(endpoint)
        .then(({ data }) => {
          const logs = data.logs || data.activity || [];
          setNotifications(logs);
          // Count events in last 1 hour as "unread"
          const oneHourAgo = Date.now() - 3600000;
          setUnread(logs.filter(l => new Date(l.createdAt).getTime() > oneHourAgo).length);
        })
        .catch(() => {});
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [role]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(!open); setUnread(0); }}
        style={{
          width: 40, height: 40, borderRadius: 12,
          background: open ? 'rgba(0,180,216,0.08)' : 'transparent',
          border: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', position: 'relative', color: '#64748b', fontSize: 18,
          transition: 'all 0.2s',
        }}
      >
        <FiBell />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            width: 18, height: 18, borderRadius: '50%',
            background: '#ef4444', color: '#fff',
            fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 48, right: 0, width: 360,
          background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0,180,216,0.1)', borderRadius: 16,
          boxShadow: '0 12px 48px rgba(0,0,0,0.1)',
          zIndex: 1000, overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 18px', borderBottom: '1px solid var(--border-color)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', fontFamily: 'Poppins, sans-serif' }}>Notifications</span>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{notifications.length} recent</span>
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No recent activity</div>
            ) : notifications.map((n, i) => (
              <div key={n._id || i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 18px',
                borderBottom: '1px solid rgba(226,232,240,0.5)', cursor: 'pointer',
                transition: 'background 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,180,216,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 32, height: 32, minWidth: 32, borderRadius: 8,
                  background: '#eef3e6', color: '#00B4D8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}>{getIcon(n.action)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                    {n.username || 'System'} — {(n.action || '').replace(/_/g, ' ')}
                  </div>
                  {n.details && <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.details}</div>}
                </div>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, flexShrink: 0 }}>{timeAgo(n.createdAt)}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          {role === 'admin' && (
            <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border-color)', textAlign: 'center' }}>
              <button
                onClick={() => { setOpen(false); navigate('/admin/audit-logs'); }}
                style={{
                  background: 'none', border: 'none', color: '#00B4D8', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >View All Activity →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
